import { useState } from 'react';

interface ShareExportPanelProps {
  prompt: string;
  style: string;
  duration: string;
  resolution: string;
  thumbnail?: string;
  darkMode: boolean;
  onLoginRequired: () => void;
}

const FAKE_SHARE_URL = 'https://1world-ai.com/share/v/a7f3x92k';
const FAKE_EMBED_CODE = (id: string) =>
  `<iframe\n  src="https://1world-ai.com/embed/v/${id}"\n  width="960" height="540"\n  frameborder="0"\n  allow="autoplay; fullscreen"\n  allowfullscreen\n  loading="lazy"\n></iframe>`;

type Tab = 'link' | 'gif' | 'embed' | 'watermark';

const WATERMARK_POSITIONS = [
  { id: 'bottom-right', label: 'Bottom Right', icon: 'ri-arrow-right-down-line' },
  { id: 'bottom-left', label: 'Bottom Left', icon: 'ri-arrow-left-down-line' },
  { id: 'top-right', label: 'Top Right', icon: 'ri-arrow-right-up-line' },
  { id: 'top-left', label: 'Top Left', icon: 'ri-arrow-left-up-line' },
  { id: 'center', label: 'Center', icon: 'ri-focus-3-line' },
];

const WATERMARK_STYLES = [
  { id: 'logo-text', label: '1WORLD-AI Logo', icon: 'ri-shield-star-line' },
  { id: 'subtle', label: 'Subtle badge', icon: 'ri-price-tag-3-line' },
  { id: 'corner-bug', label: 'Corner bug', icon: 'ri-checkbox-blank-circle-line' },
];

const WM_OPACITY_PRESETS = [25, 50, 75, 100];

function WatermarkPreview({ thumbnail, position, opacity, style, showWatermark, darkMode }: {
  thumbnail?: string;
  position: string;
  opacity: number;
  style: string;
  showWatermark: boolean;
  darkMode: boolean;
}) {
  const posClass: Record<string, string> = {
    'bottom-right': 'bottom-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'top-right': 'top-2 right-2',
    'top-left': 'top-2 left-2',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  };
  return (
    <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-video">
      {thumbnail ? (
        <img src={thumbnail} alt="Preview" className="w-full h-full object-cover" />
      ) : (
        <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
          <i className="ri-video-line text-4xl text-gray-400"></i>
        </div>
      )}
      {showWatermark && (
        <div
          className={`absolute ${posClass[position] || posClass['bottom-right']} pointer-events-none transition-all`}
          style={{ opacity: opacity / 100 }}
        >
          {style === 'logo-text' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-black/60 rounded-lg backdrop-blur-sm">
              <div className="w-3 h-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-sm flex-shrink-0"></div>
              <span className="text-white text-[10px] font-bold tracking-wide">1WORLD-AI</span>
            </div>
          )}
          {style === 'subtle' && (
            <span className="text-white text-[9px] font-medium bg-black/40 px-1.5 py-0.5 rounded backdrop-blur-sm">
              Made with 1WORLD-AI
            </span>
          )}
          {style === 'corner-bug' && (
            <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-orange-600 rounded-full flex items-center justify-center">
              <span className="text-white text-[8px] font-black">1W</span>
            </div>
          )}
        </div>
      )}
      {!showWatermark && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/50 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
            <i className="ri-lock-line text-sm"></i>No watermark — sign in required
          </div>
        </div>
      )}
    </div>
  );
}

export default function ShareExportPanel({ prompt, style, duration, resolution, thumbnail, darkMode, onLoginRequired }: ShareExportPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>('link');
  const [linkCopied, setLinkCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [gifProgress, setGifProgress] = useState<number | null>(null);
  const [gifDone, setGifDone] = useState(false);

  // Watermark state
  const [wmPosition, setWmPosition] = useState('bottom-right');
  const [wmOpacity, setWmOpacity] = useState(75);
  const [wmStyle, setWmStyle] = useState('logo-text');

  const embedId = 'a7f3x92k';
  const embedCode = FAKE_EMBED_CODE(embedId);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(FAKE_SHARE_URL).catch(() => {});
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2200);
  };

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2200);
  };

  const handleExportGif = () => {
    onLoginRequired();
  };

  const TABS: { id: Tab; label: string; icon: string; badge?: string }[] = [
    { id: 'link', label: 'Share Link', icon: 'ri-links-line' },
    { id: 'gif', label: 'GIF', icon: 'ri-image-2-line' },
    { id: 'embed', label: 'Embed', icon: 'ri-code-s-slash-line' },
    { id: 'watermark', label: 'Watermark', icon: 'ri-shield-star-line', badge: 'New' },
  ];

  const base = darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700';

  return (
    <div className={`rounded-2xl border overflow-hidden ${base}`}>
      {/* Header */}
      <div className="px-5 pt-5 pb-0 flex items-center gap-2 mb-4">
        <span className="w-5 h-5 flex items-center justify-center text-red-500">
          <i className="ri-share-forward-line text-base"></i>
        </span>
        <h3 className="text-sm font-semibold">Share &amp; Export</h3>
        <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-600'}`}>
          <i className="ri-checkbox-circle-line mr-1"></i>Ready
        </span>
      </div>

      {/* Tab bar */}
      <div className={`flex mx-5 mb-4 rounded-xl p-1 gap-0.5 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 relative flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-semibold transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-sm'
                : `${muted} hover:text-gray-700 dark:hover:text-gray-300`
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.badge && (
              <span className={`absolute -top-1 -right-1 text-[8px] px-1 py-0.5 rounded-full font-bold ${
                activeTab === tab.id ? 'bg-white/30 text-white' : 'bg-red-500 text-white'
              }`}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="px-5 pb-5">

        {/* Share Link tab */}
        {activeTab === 'link' && (
          <div className="space-y-4">
            <p className={`text-xs ${muted}`}>Anyone with this link can view your generated video — no account required.</p>
            <div className="flex gap-2">
              <div className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm truncate ${inputBg}`}>
                <i className={`ri-lock-unlock-line flex-shrink-0 ${muted}`}></i>
                <span className="truncate">{FAKE_SHARE_URL}</span>
              </div>
              <button
                onClick={handleCopyLink}
                className={`px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  linkCopied
                    ? darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600'
                    : 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-md'
                }`}
              >
                <i className={`${linkCopied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                {linkCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <div>
              <p className={`text-xs font-semibold mb-2 ${muted}`}>Share directly to</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { icon: 'ri-twitter-x-line', label: 'Twitter/X', color: darkMode ? 'bg-gray-900 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200' },
                  { icon: 'ri-linkedin-line', label: 'LinkedIn', color: darkMode ? 'bg-blue-900/30 hover:bg-blue-900/50' : 'bg-blue-50 hover:bg-blue-100' },
                  { icon: 'ri-whatsapp-line', label: 'WhatsApp', color: darkMode ? 'bg-green-900/30 hover:bg-green-900/50' : 'bg-green-50 hover:bg-green-100' },
                ].map(btn => (
                  <button
                    key={btn.label}
                    onClick={onLoginRequired}
                    className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-medium transition-all cursor-pointer ${btn.color} ${muted}`}
                  >
                    <i className={`${btn.icon} text-lg`}></i>
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={`rounded-xl p-3 border flex flex-wrap gap-2 ${darkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
              {[style, duration, resolution].map(tag => (
                <span key={tag} className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white border border-gray-200 text-gray-600'}`}>{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* Export GIF tab */}
        {activeTab === 'gif' && (
          <div className="space-y-4">
            <p className={`text-xs ${muted}`}>Convert your video to an animated GIF — perfect for social posts, docs, and presentations.</p>

            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Size', value: '480p', icon: 'ri-aspect-ratio-line' },
                { label: 'FPS', value: '24 fps', icon: 'ri-speed-line' },
                { label: 'Length', value: duration, icon: 'ri-timer-line' },
              ].map(opt => (
                <div key={opt.label} className={`rounded-xl p-3 text-center border ${darkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                  <i className={`${opt.icon} text-red-500 text-base block mb-1`}></i>
                  <span className={`block text-[10px] font-medium ${muted}`}>{opt.label}</span>
                  <span className={`block text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{opt.value}</span>
                </div>
              ))}
            </div>

            {gifProgress !== null && (
              <div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className={gifDone ? 'text-green-500 font-semibold' : muted}>
                    {gifDone ? 'GIF ready!' : 'Converting…'}
                  </span>
                  <span className={muted}>{Math.round(gifProgress)}%</span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-200 ${gifDone ? 'bg-green-500' : 'bg-gradient-to-r from-red-500 to-orange-500'}`}
                    style={{ width: `${gifProgress}%` }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleExportGif}
              className="w-full py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:shadow-md transition-all"
            >
              <i className="ri-image-2-line text-base"></i>Export as GIF
            </button>
          </div>
        )}

        {/* Embed tab */}
        {activeTab === 'embed' && (
          <div className="space-y-4">
            <p className={`text-xs ${muted}`}>Paste this snippet into any webpage to embed your video as a responsive iframe player.</p>

            <div className={`relative rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`flex items-center justify-between px-3 py-2 border-b text-xs font-semibold ${darkMode ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
                <span><i className="ri-html5-line mr-1.5 text-orange-500"></i>HTML snippet</span>
                <button
                  onClick={handleCopyEmbed}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${
                    embedCopied
                      ? darkMode ? 'text-green-400' : 'text-green-600'
                      : 'text-red-500 hover:underline'
                  }`}
                >
                  <i className={`${embedCopied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}></i>
                  {embedCopied ? 'Copied!' : 'Copy code'}
                </button>
              </div>
              <pre className={`p-3 text-xs leading-relaxed overflow-x-auto font-mono ${darkMode ? 'bg-gray-950 text-green-400' : 'bg-gray-50 text-gray-700'}`}>{embedCode}</pre>
            </div>

            <div className={`rounded-xl p-3 border space-y-2 ${darkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
              <p className={`text-xs font-semibold ${muted}`}>Embed specs</p>
              {[
                { label: 'Default size', value: '960 × 540 px' },
                { label: 'Responsive', value: 'Yes — scales to container' },
                { label: 'Autoplay', value: 'On mute (policy-compliant)' },
              ].map(row => (
                <div key={row.label} className="flex justify-between text-xs">
                  <span className={muted}>{row.label}</span>
                  <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Watermark tab */}
        {activeTab === 'watermark' && (
          <div className="space-y-5">
            {/* Login gate banner */}
            <div className={`flex items-start gap-3 p-3 rounded-xl border ${
              darkMode ? 'bg-amber-900/15 border-amber-800' : 'bg-amber-50 border-amber-200'
            }`}>
              <i className={`ri-shield-star-line text-lg flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}></i>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Watermark applied automatically
                </p>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-amber-500' : 'text-amber-600'}`}>
                  Free accounts include a 1WORLD-AI watermark.{' '}
                  <button onClick={onLoginRequired} className="underline font-semibold">Sign in</button>
                  {' '}to remove it or customize the style and position.
                </p>
              </div>
            </div>

            {/* Live preview */}
            <div>
              <p className={`text-xs font-semibold mb-2 ${muted}`}>
                <i className="ri-eye-line mr-1"></i>Live Preview
              </p>
              <WatermarkPreview
                thumbnail={thumbnail}
                position={wmPosition}
                opacity={wmOpacity}
                style={wmStyle}
                showWatermark={true}
                darkMode={darkMode}
              />
            </div>

            {/* Style selector */}
            <div>
              <p className={`text-xs font-semibold mb-2 ${muted}`}>Watermark Style</p>
              <div className="grid grid-cols-3 gap-2">
                {WATERMARK_STYLES.map(s => (
                  <button
                    key={s.id}
                    onClick={onLoginRequired}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all relative ${
                      wmStyle === s.id
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                        : darkMode ? 'border-gray-700 bg-gray-800 hover:border-gray-600' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    title="Sign in to customize"
                  >
                    <i className={`${s.icon} text-lg ${wmStyle === s.id ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                    <span className={`text-[10px] font-medium text-center ${wmStyle === s.id ? 'text-red-600 dark:text-red-400' : darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{s.label}</span>
                    <div className="absolute top-1 right-1 w-3 h-3 flex items-center justify-center">
                      <i className={`ri-lock-line text-[9px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Position selector */}
            <div>
              <p className={`text-xs font-semibold mb-2 ${muted}`}>Position</p>
              <div className="grid grid-cols-5 gap-1.5">
                {WATERMARK_POSITIONS.map(p => (
                  <button
                    key={p.id}
                    onClick={onLoginRequired}
                    title={`${p.label} — sign in to customize`}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                      wmPosition === p.id
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-500'
                        : darkMode ? 'border-gray-700 text-gray-500 hover:border-gray-600' : 'border-gray-200 text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${p.icon} text-base`}></i>
                    <span className="text-[9px] font-medium leading-tight text-center">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Opacity */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs font-semibold ${muted}`}>Opacity</p>
                <span className={`text-xs font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{wmOpacity}%</span>
              </div>
              <div className="flex gap-2">
                {WM_OPACITY_PRESETS.map(o => (
                  <button
                    key={o}
                    onClick={onLoginRequired}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      wmOpacity === o
                        ? 'bg-red-500 text-white border-red-500'
                        : darkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {o}%
                  </button>
                ))}
              </div>
            </div>

            {/* Remove watermark CTA */}
            <button
              onClick={onLoginRequired}
              className="w-full py-3 bg-gradient-to-r from-red-500 to-orange-600 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-red-500/30 transition-all"
            >
              <i className="ri-shield-cross-line text-base"></i>
              Remove Watermark — Sign In Free
            </button>

            <p className={`text-center text-[11px] ${muted}`}>
              <i className="ri-information-line mr-0.5"></i>
              Pro users can remove watermarks and add custom branding
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
